class StorefrontSearch extends SearchForm {
  constructor() {
    super();
    this.storefrontToken = this.dataset.storefrontToken;
    this.shopDomain = this.dataset.shopDomain;
    this.resultsContainer = this.querySelector('[data-storefront-search-results]');
    this.isOpen = false;
    this.searchTerm = '';
    this.cachedResults = {};
    this.abortController = new AbortController();
    this.setupStorefrontListeners();
  } 

  setupStorefrontListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const query = this.getQuery();
    this.searchTerm = query;

    if (!query.length) {
      this.close(true);
      return;
    }

    this.fetchProducts(query);
  }

  onFormSubmit(event) {
    event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.close();
    }
  }

  onFocus() {
    if (this.searchTerm && this.resultsContainer.innerHTML) this.open();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close(true);
  }

  async fetchProducts(query) {
    const cacheKey = query.toLowerCase();

    if (this.cachedResults[cacheKey]) {
      this.renderResults(this.cachedResults[cacheKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();
    this.setAttribute('loading', true);
    this.resultsContainer.innerHTML = `<div style="display:flex;justify-content:center;padding:1.5rem;">
      <svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" style="width:2rem;height:2rem;">
        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
      </svg>
    </div>`;
    this.resultsContainer.style.display = 'block';
    this.resultsContainer.style.maxHeight = `${this.getResultsMaxHeight()}px`;

    try {
      const response = await fetch(`https://${this.shopDomain}/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({
          query: `query ProductSearch($q: String!) {
            products(first: 20, query: $q) {
              edges {
                node {
                  id
                  title
                  handle
                  description
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  images(first: 1) {
                    edges {
                      node {
                        url
                        altText
                      }
                    }
                  }
                }
              }
            }
          }`,
          variables: { q: query },
        }),
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error(response.status);

      const { data } = await response.json();
      const q = query.toLowerCase();
      const filtered = data.products.edges.filter(({ node }) =>
        node.title.toLowerCase().includes(q) ||
        (q.length >= 3 && node.description.toLowerCase().includes(q))
      );
      const sorted = filtered.sort((a, b) => {
        const aTitle = a.node.title.toLowerCase().includes(q);
        const bTitle = b.node.title.toLowerCase().includes(q);
        if (aTitle && !bTitle) return -1;
        if (!aTitle && bTitle) return 1;
        return 0;
      });
      this.cachedResults[cacheKey] = sorted;
      this.renderResults(sorted);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.removeAttribute('loading');
      console.error('[StorefrontSearch]', error);
    }
  }

  renderResults(edges) {
    this.removeAttribute('loading');

    if (!edges.length) {
      this.resultsContainer.innerHTML = '<p style="padding: 1rem 1.5rem;">No results found.</p>';
      this.open();
      return;
    }

    this.resultsContainer.innerHTML = edges
      .map(({ node }) => {
        const image = node.images.edges[0]?.node;
        const price = new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: node.priceRange.minVariantPrice.currencyCode,
        }).format(node.priceRange.minVariantPrice.amount);
        return `<div style="padding: 0.75rem 1.5rem; border-bottom: 1px solid rgba(0,0,0,.08);">
          <a href="/products/${node.handle}" style="display: flex; gap: 1rem; align-items: center; text-decoration: none; color: inherit;">
            ${image ? `<img src="${image.url}" alt="${image.altText || ''}" width="48" height="48" style="object-fit: cover; flex-shrink: 0;">` : ''}
            <span style="flex: 1;">${node.title}</span>
            <span>${price}</span>
          </a>
        </div>`;
      })
      .join('');

    this.open();
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header');
    return header ? window.innerHeight - header.getBoundingClientRect().bottom : window.innerHeight;
  }

  open() {
    this.resultsContainer.style.display = 'block';
    this.resultsContainer.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.toggleResetButton();
    }
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.isOpen = false;
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.style.display = '';
    this.resultsContainer.style.maxHeight = '';
  }
}

customElements.define('custom-storefront-search', StorefrontSearch);
